/* eslint-disable @typescript-eslint/no-unused-vars */

import axiosInstance from 'api/axiosInstance/axiosInstance'
import axios from 'axios'
import {
  defaultContact,
  Contact,
  AuctionDTO,
  Entity,
  AuctionLightDTO,
  Vehicle,
  defaultVehicle,
  Translator,
  FilesUrlResponse,
  VehicleUpdateResponse,
  Schema,
  SchemaAndIntervals,
  DeepPartial,
  Company,
  CompaniesSearchRequest,
  CompaniesSearchResponse,
  PaginatedVehiclesResponse,
  VehicleListResponse,
} from 'models'
import { ENDPOINTS, PATHS, defaultSchema, defaultSchemaAndIntervals } from 'utils/constants'
import { dummyHistoryResponse, HistoryCreateBody, HistoryDTO } from 'models/dtos/history'
import { apiSingleFlight } from 'utils/http/queues'
import { withRetry } from 'utils/http/retry'
import { shouldRetry } from 'utils/http/shouldRetry'
import { OdataFilterBuilder } from '../filters'
import { IQueries, VehicleListRequestOptions, VehiclesRequestOptions } from './queryInterface'
import { parseCompaniesSearchResponse, sanitizeCompaniesRequest } from './companiesSearch'
import { parseVehicleListLightResponse, parseVehicleListResponse } from './vehicleListSearch'
import { parseVehiclesSearchResponse } from './vehiclesSearch'

const escapeOdataString = (s: string) => s.replace(/'/g, "''")

const buildEntityIdOrFilter = (ids: string[]) => {
  const parts = ids.filter(Boolean).map((id) => `entityId eq '${escapeOdataString(id)}'`)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0]
  return `(${parts.join(' or ')})`
}

const buildEntityIdInFilter = (ids: string[]) => {
  const quotedIds = ids.filter(Boolean).map((id) => `'${escapeOdataString(id)}'`)
  if (quotedIds.length === 0) return ''
  return `entityId in (${quotedIds.join(',')})`
}

const withDefaultVehiclesOptions = (options?: VehiclesRequestOptions) => ({
  limit: options?.limit ?? 1500,
  page: options?.page ?? 0,
  signal: options?.signal,
})

const withDefaultVehicleListOptions = (options?: VehicleListRequestOptions) => ({
  limit: options?.limit ?? 1500,
  offset: options?.offset ?? 0,
  signal: options?.signal,
})

const VEHICLE_RETRY_OPTIONS = {
  retries: 6,
  baseDelayMs: 300,
  maxDelayMs: 10_000,
  jitterRatio: 0.25,
}

const EMPTY_VEHICLES_RESPONSE: PaginatedVehiclesResponse = { items: [], totalCount: 0 }
const EMPTY_VEHICLES_LIST_RESPONSE: VehicleListResponse = { items: [], totalCount: 0 }

const mapAxiosError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    return new Error(error.response?.data?.message || error.message || 'Unknown Axios error')
  }
  return new Error('An unexpected error occurred')
}

const executeRequest = async <T>(
  call: () => Promise<{ data: T; status: number; statusText: string }>,
  okStatuses: Array<number> = [200],
): Promise<T> => {
  try {
    const response = await call()
    if (!okStatuses.includes(response.status)) {
      throw new Error(`HTTP Error: ${response.status} - ${response.statusText}`)
    }
    return response.data
  } catch (error) {
    throw mapAxiosError(error)
  }
}

const buildCombinedFilter = (filters: Array<string>, entitiesFilter: string) => {
  if (!entitiesFilter) return ''
  if (filters.length === 0) return entitiesFilter
  const normalized = filters.map((part) => `(${part})`).join(' and ')
  return `${normalized} and ${entitiesFilter}`
}

const executeVehiclesRequest = async (
  filter: string,
  options?: VehiclesRequestOptions,
): Promise<PaginatedVehiclesResponse> => {
  if (!filter) {
    return EMPTY_VEHICLES_RESPONSE
  }
  const { limit, page, signal } = withDefaultVehiclesOptions(options)
  const requestConfig = {
    params: {
      filter,
      limit,
      page,
    },
    signal,
  }

  try {
    return await apiSingleFlight(() =>
      withRetry(
        async () => {
          const response = await axiosInstance.get<unknown>('/vehicles', requestConfig)
          if (response.status !== 200) {
            throw new Error(`HTTP Error: ${response.status} - ${response.statusText}`)
          }
          return parseVehiclesSearchResponse(response.data)
        },
        shouldRetry,
        VEHICLE_RETRY_OPTIONS,
      ),
    )
  } catch (error) {
    throw mapAxiosError(error)
  }
}

const queryAdapter: IQueries = {
  getIp: async () => executeRequest(() => axiosInstance.get('/ipAddress')),

  getHolidays: async () => {
    try {
      const response = await axios.get('https://calendrier.api.gouv.fr/jours-feries/metropole.json')
      if (response.status !== 200) {
        throw new Error(`HTTP Error: ${response.status} - ${response.statusText}`)
      }
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || error.message || 'Unknown Axios error')
      }
      throw new Error('An unexpected error occurred')
    }
  },

  getUser: async () => executeRequest(() => axios.get(PATHS.userInfo)),

  getAuction: async (auctionNo: string, filteringEntitiesIds: Array<Entity['id']>) => {
    const queryParams = {
      params: {
        auction: auctionNo,
        filteringEntities: filteringEntitiesIds.toString(),
      },
    }
    return executeRequest(() => axiosInstance.get<AuctionDTO>('/auction', queryParams))
  },

  getAuctionsLight: async (filteringEntityId: Entity['id'], timeLine) => {
    const queryFilter = new OdataFilterBuilder()
      .entityIdFilter(filteringEntityId)
      .and()
      .timeLineFilter(timeLine)
      .build()

    const filterParams = {
      params: {
        filter: queryFilter,
        limit: 1000000,
        page: 0,
      },
    }
    return executeRequest(() =>
      axiosInstance.get<Array<AuctionLightDTO>>(ENDPOINTS.AUCTIONS_LIGHT, filterParams),
    )
  },

  /**
   * STOCK (compat: 1 entity) -> délègue au batch
   */
  getStockFromEntity: async (filteringEntityId: Entity['id']): Promise<Vehicle[]> => {
    if (!filteringEntityId) return []
    const { items } = await queryAdapter.getStockFromEntities([filteringEntityId])
    return items
  },

  /**
   * STOCK (batch): multiple entityIds in ONE request (OData OR)
   * Protected with concurrency=1 + exponential backoff retry for 429/transient 5xx
   */
  getStockFromEntities: async (
    filteringEntitiesIds: Array<Entity['id']>,
    options?: VehiclesRequestOptions,
  ): Promise<PaginatedVehiclesResponse> => {
    const ids = filteringEntitiesIds.filter(Boolean)
    if (ids.length === 0) return EMPTY_VEHICLES_RESPONSE

    const entitiesOr = buildEntityIdOrFilter(ids)
    if (!entitiesOr) return EMPTY_VEHICLES_RESPONSE

    const statusFilter = 'status/generalStatus not in (3,4,6,8)'
    const filter = buildCombinedFilter([statusFilter], entitiesOr)
    return executeVehiclesRequest(filter, options)
  },

  /**
   * WAITING FILES (compat: 1 entity) -> délègue au batch
   */
  getWaitingFilesFromEntity: async (filteringEntityId: Entity['id']): Promise<Vehicle[]> => {
    if (!filteringEntityId) return []
    const { items } = await queryAdapter.getWaitingFilesFromEntities([filteringEntityId])
    return items
  },

  /**
   * WAITING FILES (batch): multiple entityIds in ONE request (OData OR)
   * Protected with concurrency=1 + exponential backoff retry for 429/transient 5xx
   */
  getWaitingFilesFromEntities: async (
    filteringEntitiesIds: Array<Entity['id']>,
    options?: VehiclesRequestOptions,
  ): Promise<PaginatedVehiclesResponse> => {
    const ids = filteringEntitiesIds.filter(Boolean)
    if (ids.length === 0) return EMPTY_VEHICLES_RESPONSE

    const entitiesOr = buildEntityIdOrFilter(ids)
    if (!entitiesOr) return EMPTY_VEHICLES_RESPONSE

    const statusFilter = 'status/generalStatus eq 7'
    const filter = buildCombinedFilter([statusFilter], entitiesOr)
    return executeVehiclesRequest(filter, options)
  },

  getAllEntities: async (): Promise<Array<Entity>> =>
    executeRequest(() => axiosInstance.get<Array<Entity>>('/entities')),

  searchCompanies: async (request: CompaniesSearchRequest): Promise<CompaniesSearchResponse> => {
    const sanitizedRequest = sanitizeCompaniesRequest(request)

    const response = await executeRequest(() =>
      axiosInstance.post<unknown>('/companies', sanitizedRequest),
    )

    return parseCompaniesSearchResponse(response)
  },

  getCompanyById: async (companyId: string): Promise<Company> => {
    if (!companyId) {
      throw new Error('Company id is required')
    }

    return executeRequest(() =>
      axiosInstance.get<Company>(`/companies/${encodeURIComponent(companyId)}`),
    )
  },

  // TODO: Readapt once eq filters on strings are OK
  getVehicles: async (
    filteringEntitiesIds: Array<Entity['id']>,
    key: 'no' | 'licensePlate' | 'vin',
    value: string,
    options?: VehiclesRequestOptions,
  ) => {
    if (key === 'no') {
      return { items: [defaultVehicle], totalCount: 1 }
    }

    const ids = filteringEntitiesIds.filter(Boolean).slice(0, 15)
    if (ids.length === 0) return EMPTY_VEHICLES_RESPONSE

    const entitiesOr = buildEntityIdOrFilter(ids)
    if (!entitiesOr) return EMPTY_VEHICLES_RESPONSE

    const searchFilters: Record<'licensePlate' | 'vin', string> = {
      licensePlate: `contains(vehiclePartial/licensePlate, '${escapeOdataString(value)}')`,
      vin: `contains(vehiclePartial/vin, '${escapeOdataString(value)}')`,
    }

    const searchKey: 'licensePlate' | 'vin' = key === 'vin' ? 'vin' : 'licensePlate'
    const filter = buildCombinedFilter([searchFilters[searchKey]], entitiesOr)

    const effectiveOptions: VehiclesRequestOptions = {
      ...options,
      limit: options?.limit ?? 10,
    }

    return executeVehiclesRequest(filter, effectiveOptions)
  },

  getVehicleListLight: async (
    no: string,
    filteringEntitiesIds: Array<Entity['id']>,
    options?: VehicleListRequestOptions,
  ): Promise<VehicleListResponse> => {
    const normalizedNo = no.trim()
    if (!normalizedNo) return EMPTY_VEHICLES_LIST_RESPONSE

    const ids = filteringEntitiesIds.filter(Boolean)
    if (ids.length === 0) return EMPTY_VEHICLES_LIST_RESPONSE

    const entitiesIn = buildEntityIdInFilter(ids)
    if (!entitiesIn) return EMPTY_VEHICLES_LIST_RESPONSE

    const filter = `auction/no eq '${escapeOdataString(normalizedNo)}' and ${entitiesIn}`
    const { limit, offset, signal } = withDefaultVehicleListOptions(options)

    const queryParams = {
      params: {
        filter,
        offset,
        limit,
      },
      signal,
    }

    const response = await executeRequest(() =>
      axiosInstance.get<unknown>(ENDPOINTS.VEHICLES_LIST_LIGHT, queryParams),
    )

    return parseVehicleListLightResponse(response)
  },

  getVehicleListFull: async (
    no: string,
    filteringEntitiesIds: Array<Entity['id']>,
    options?: VehicleListRequestOptions,
  ): Promise<VehicleListResponse> => {
    const normalizedNo = no.trim()
    if (!normalizedNo) return EMPTY_VEHICLES_LIST_RESPONSE

    const ids = filteringEntitiesIds.filter(Boolean)
    if (ids.length === 0) return EMPTY_VEHICLES_LIST_RESPONSE

    const entitiesIn = buildEntityIdInFilter(ids)
    if (!entitiesIn) return EMPTY_VEHICLES_LIST_RESPONSE

    const filter = `auction/no eq '${escapeOdataString(normalizedNo)}' and ${entitiesIn}`
    const { limit, offset, signal } = withDefaultVehicleListOptions(options)
    const page = limit > 0 ? Math.floor(offset / limit) : 0

    const queryParams = {
      params: {
        filter,
        page,
        limit,
      },
      signal,
    }

    const response = await executeRequest(() =>
      axiosInstance.get<unknown>('/vehicles', queryParams),
    )

    return parseVehicleListResponse(response, 'vehicles/list/full')
  },

  getVehicleByNo: async (no) => {
    if (!no) {
      return defaultVehicle
    }
    const queryParams = {
      params: {
        vehicleNo: no,
      },
    }
    return executeRequest(() => axiosInstance.get<Vehicle>('/vehicleByNo', { ...queryParams }))
  },

  getStatusesObject: async () => {
    const queryParams = {
      params: {
        mode: 'object',
      },
    }
    return executeRequest(() =>
      axiosInstance.get<Translator>(ENDPOINTS.TRANSLATOR_OBJECT, queryParams),
    )
  },

  getFilesStorageUrl: async (ids) => {
    const listOfIdsObjects = ids.map((photoId) => ({ id: photoId }))

    const queryBody = {
      ids: listOfIdsObjects,
    }

    return executeRequest(() => axiosInstance.post<FilesUrlResponse>(ENDPOINTS.FILES, queryBody))
  },

  updateVehicle: async (body: DeepPartial<Vehicle>) =>
    executeRequest(() => axiosInstance.post<VehicleUpdateResponse>(ENDPOINTS.VEHICLE_UPDATE, body)),

  updateSchema: async (body) =>
    executeRequest(() => axiosInstance.post<string>(ENDPOINTS.UPDATE_SCHEMA, body)),

  getSchema: async (userId: string, companyId: string) => {
    const queryParams = {
      params: {
        userId,
        companyId,
      },
    }

    try {
      const response = await axiosInstance.get<SchemaAndIntervals>(
        ENDPOINTS.GET_SCHEMA,
        queryParams,
      )

      if (response.status === 204) {
        return defaultSchemaAndIntervals
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          return defaultSchemaAndIntervals
        }
        throw mapAxiosError(error)
      }
      throw mapAxiosError(error)
    }
  },

  getHistory: async (historyParams: string) => {
    const endpoint = `${ENDPOINTS.HISTORY}${historyParams}`
    return dummyHistoryResponse
  },

  getMongo: async (filteringEntity: Entity['id']) => {
    const queryParams = {
      params: {
        filteringEntity,
      },
    }
    return executeRequest(() => axiosInstance.get<Array<HistoryDTO>>(ENDPOINTS.MONGO, queryParams))
  },

  postReserveHistory: async (body: HistoryCreateBody) =>
    executeRequest(() => axiosInstance.post(ENDPOINTS.POST_RESERVE_HISTORY, body), [201]),
}

export { queryAdapter }
